// Profit and Loss: Xero's figures for any range of days, regrouped into revenue,
// gross profit, true operational costs, EBITDA, expense loss, interest, tax
// allowance and net, beside up to 12 earlier months, quarters or years.
//
// Every figure, label and the check against Xero's own net profit come from
// xero_pnl_view_v1 (db/migrations/8610). The page chooses dates and colours and
// works out nothing. CLAUDE.md rule 4. Open to can_push_xero only.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, LineChart, RefreshCw } from 'lucide-react'
import { useGetProfitAndLoss } from '../hooks/backend/finance'
import { Button } from '../lib/shadcn/button'
import { Spinner } from '../lib/shadcn/spinner'

type Num = number | null

type Period = {
  from: string
  to: string
  label: string
  pulledAt: string | null
  xeroNetProfit: Num
  difference: Num
  taxRate: Num
}

type Account = { code: string; name: string; byType: boolean; values: Num[] }

type Line = {
  key: string
  label: string
  kind: 'line' | 'subtotal' | 'percent' | 'total'
  values: Num[]
  accounts: Account[]
}

type Pnl = { periods: Period[]; lines: Line[]; pending: number; xeroBusy: boolean }

type Compare = 'none' | 'month' | 'quarter' | 'year'

// Fleetly's financial year starts on 1 March.
const FY_START_MONTH = 2

function todaySast(): Date {
  const s = new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Johannesburg' }).format(new Date())
  return new Date(`${s}T00:00:00Z`)
}

const iso = (d: Date) => d.toISOString().slice(0, 10)
const ymd = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d))

type Preset = { key: string; label: string; range: () => [string, string] }

const PRESETS: Preset[] = [
  { key: 'this-month', label: 'This month', range: () => { const t = todaySast(); return [iso(ymd(t.getUTCFullYear(), t.getUTCMonth(), 1)), iso(t)] } },
  { key: 'last-month', label: 'Last month', range: () => { const t = todaySast(); return [iso(ymd(t.getUTCFullYear(), t.getUTCMonth() - 1, 1)), iso(ymd(t.getUTCFullYear(), t.getUTCMonth(), 0))] } },
  { key: 'this-quarter', label: 'This quarter', range: () => { const t = todaySast(); const q = Math.floor(t.getUTCMonth() / 3) * 3; return [iso(ymd(t.getUTCFullYear(), q, 1)), iso(t)] } },
  { key: 'last-quarter', label: 'Last quarter', range: () => { const t = todaySast(); const q = Math.floor(t.getUTCMonth() / 3) * 3; return [iso(ymd(t.getUTCFullYear(), q - 3, 1)), iso(ymd(t.getUTCFullYear(), q, 0))] } },
  { key: 'fy-to-date', label: 'Financial year to date', range: () => { const t = todaySast(); const y = t.getUTCMonth() >= FY_START_MONTH ? t.getUTCFullYear() : t.getUTCFullYear() - 1; return [iso(ymd(y, FY_START_MONTH, 1)), iso(t)] } },
  { key: 'last-fy', label: 'Last financial year', range: () => { const t = todaySast(); const y = (t.getUTCMonth() >= FY_START_MONTH ? t.getUTCFullYear() : t.getUTCFullYear() - 1) - 1; return [iso(ymd(y, FY_START_MONTH, 1)), iso(ymd(y + 1, FY_START_MONTH, 0))] } },
]

const COMPARE_LABEL: Record<Compare, string> = {
  none: 'No comparison',
  month: 'Earlier months',
  quarter: 'Earlier quarters',
  year: 'Earlier years',
}

const rand = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const cents = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function money(v: Num): string {
  if (v === null || v === undefined) return '—'
  const n = Number(v)
  return `${n < 0 ? '-' : ''}R${rand.format(Math.abs(n))}`
}

function exact(v: Num): string | undefined {
  if (v === null || v === undefined) return undefined
  const n = Number(v)
  return `${n < 0 ? '-' : ''}R${cents.format(Math.abs(n))}`
}

function percent(v: Num): string {
  return v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`
}

function change(a: Num, b: Num, kind: Line['kind']): string {
  if (a === null || b === null || a === undefined || b === undefined) return '—'
  const d = Number(a) - Number(b)
  if (kind === 'percent') return `${d >= 0 ? '+' : ''}${d.toFixed(1)} pts`
  const pct = Number(b) !== 0 ? ` (${d >= 0 ? '+' : ''}${((d / Math.abs(Number(b))) * 100).toFixed(0)}%)` : ''
  return `${d >= 0 ? '+' : '-'}R${rand.format(Math.abs(d))}${pct}`
}

const inputCls = 'h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground'

export default function ProfitAndLoss() {
  const query = useGetProfitAndLoss<Pnl>()
  const load = query.trigger
  const [params, setParams] = useSearchParams()
  const initial = PRESETS[1].range()
  const from = params.get('from') ?? initial[0]
  const to = params.get('to') ?? initial[1]
  const compare = (['none', 'month', 'quarter', 'year'].includes(params.get('compare') ?? '') ? params.get('compare') : 'month') as Compare
  const count = Math.max(1, Math.min(12, Number(params.get('count') ?? 2) || 2))
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null)

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) next.set(k, v)
    setParams(next, { replace: true })
  }

  const fetchPnl = useCallback(
    (refresh: boolean) => {
      if (retry.current) clearTimeout(retry.current)
      void load({ from, to, compare, count: compare === 'none' ? 0 : count, refresh }, { skipCache: true }).catch(() => undefined)
    },
    [load, from, to, compare, count],
  )

  useEffect(() => {
    if (from && to && from <= to) fetchPnl(false)
  }, [fetchPnl, from, to])

  // Periods the database could not read inside one request come on the next.
  const data = query.data
  useEffect(() => {
    if (!data || data.pending <= 0 || query.loading) return
    retry.current = setTimeout(() => fetchPnl(false), data.xeroBusy ? 30_000 : 1_000)
    return () => { if (retry.current) clearTimeout(retry.current) }
  }, [data, query.loading, fetchPnl])

  const periods = data?.periods ?? []
  const showChange = periods.length > 1
  const allTie = periods.length > 0 && periods.every((p) => p.difference === 0)
  const lastPulled = useMemo(() => {
    const ts = periods.map((p) => p.pulledAt).filter(Boolean) as string[]
    if (!ts.length) return null
    const oldest = ts.sort()[0]
    return new Intl.DateTimeFormat('en-ZA', { timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(oldest))
  }, [periods])

  const badRange = !from || !to || from > to

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex flex-wrap items-center gap-3 border-b border-border bg-card px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <LineChart className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1 basis-60">
          <h1 className="text-lg font-semibold text-foreground">Profit and Loss</h1>
          <p className="text-xs text-muted-foreground">
            Read live from Xero and regrouped: what owners carry sits in cost of sales, what Fleetly carried for nobody sits in expense loss, below EBITDA.
          </p>
        </div>
        {lastPulled && <span className="text-xs text-muted-foreground">Read from Xero {lastPulled}</span>}
        <Button size="sm" variant="outline" disabled={query.loading || badRange} onClick={() => fetchPnl(true)}>
          <RefreshCw className={query.loading ? 'mr-1 h-4 w-4 animate-spin' : 'mr-1 h-4 w-4'} /> Refresh from Xero
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 border-b border-border bg-card px-4 py-3 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Period</span>
          <select
            className={inputCls}
            value={PRESETS.find((p) => { const [f, t] = p.range(); return f === from && t === to })?.key ?? 'custom'}
            onChange={(e) => { const p = PRESETS.find((x) => x.key === e.target.value); if (p) { const [f, t] = p.range(); set({ from: f, to: t }) } }}
          >
            {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            <option value="custom">Custom dates</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">From</span>
          <input type="date" className={inputCls} value={from} onChange={(e) => set({ from: e.target.value })} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">To</span>
          <input type="date" className={inputCls} value={to} onChange={(e) => set({ to: e.target.value })} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Compare with</span>
          <select className={inputCls} value={compare} onChange={(e) => set({ compare: e.target.value })}>
            {(Object.keys(COMPARE_LABEL) as Compare[]).map((c) => <option key={c} value={c}>{COMPARE_LABEL[c]}</option>)}
          </select>
        </label>
        {compare !== 'none' && (
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">How many</span>
            <select className={inputCls} value={count} onChange={(e) => set({ count: e.target.value })}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl space-y-3 p-3 sm:p-4">
          {badRange && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
              Choose a from date on or before the to date.
            </div>
          )}
          {query.error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">
              {query.error}
            </div>
          )}
          {data && data.pending > 0 && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-card px-4 py-2 text-sm text-muted-foreground">
              <Spinner className="h-4 w-4" />
              {data.xeroBusy
                ? `Xero is answering too many requests just now. ${data.pending} more period${data.pending === 1 ? '' : 's'} will be read in 30 seconds.`
                : `Reading ${data.pending} more period${data.pending === 1 ? '' : 's'} from Xero.`}
            </div>
          )}
          {!data && query.loading && (
            <p className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Reading from Xero…</p>
          )}

          {data && (
            <div className="overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="sticky left-0 bg-card px-3 py-2 text-left font-medium" />
                    {periods.map((p, i) => (
                      <th key={`${p.from}-${p.to}`} className={`whitespace-nowrap px-3 py-2 text-right font-medium ${i === 0 ? 'text-foreground' : ''}`}>{p.label}</th>
                    ))}
                    {showChange && <th className="whitespace-nowrap px-3 py-2 text-right font-medium">Change on {periods[1].label}</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((l) => {
                    const expandable = l.kind === 'line' && l.accounts.length > 0
                    const isOpen = open[l.key] === true
                    const strong = l.kind === 'subtotal' || l.kind === 'total'
                    const rowCls =
                      l.kind === 'total' ? 'border-t-2 border-foreground/30 font-semibold'
                      : l.kind === 'subtotal' ? 'border-t border-border font-semibold bg-muted/30'
                      : l.kind === 'percent' ? 'text-xs italic text-muted-foreground'
                      : 'border-t border-border/50'
                    return (
                      <Fragment key={l.key}>
                        <tr className={rowCls}>
                          <td className="sticky left-0 bg-card px-3 py-1.5">
                            {expandable ? (
                              <button type="button" className="flex items-center gap-1 text-left hover:underline" onClick={() => setOpen((o) => ({ ...o, [l.key]: !isOpen }))}>
                                {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                {l.label}
                              </button>
                            ) : (
                              <span className={l.kind === 'line' ? 'pl-[18px]' : ''}>{l.label}</span>
                            )}
                          </td>
                          {l.values.map((v, i) => (
                            <td
                              key={i}
                              title={l.kind === 'percent' ? undefined : exact(v)}
                              className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${strong && v !== null && Number(v) < 0 ? 'text-rose-700 dark:text-rose-400' : ''}`}
                            >
                              {l.kind === 'percent' ? percent(v) : money(v)}
                            </td>
                          ))}
                          {showChange && (
                            <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-muted-foreground">{change(l.values[0], l.values[1], l.kind)}</td>
                          )}
                        </tr>
                        {isOpen && l.accounts.map((a) => (
                          <tr key={`${l.key}-${a.code}-${a.name}`} className="text-xs text-muted-foreground">
                            <td className="sticky left-0 bg-card py-1 pl-10 pr-3">
                              {a.code ? `${a.code} ` : ''}{a.name}
                              {a.byType && <span className="ml-2 rounded bg-amber-100 px-1 text-[10px] text-amber-900 dark:bg-amber-900 dark:text-amber-100">placed by Xero type</span>}
                            </td>
                            {a.values.map((v, i) => (
                              <td key={i} title={exact(v)} className="whitespace-nowrap px-3 py-1 text-right tabular-nums">{money(v)}</td>
                            ))}
                            {showChange && <td className="whitespace-nowrap px-3 py-1 text-right tabular-nums">{change(a.values[0], a.values[1], 'line')}</td>}
                          </tr>
                        ))}
                      </Fragment>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border text-xs">
                    <td className="sticky left-0 bg-card px-3 py-2 text-muted-foreground">Net profit in Xero</td>
                    {periods.map((p) => (
                      <td key={`${p.from}-${p.to}`} title={exact(p.xeroNetProfit)} className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                        {money(p.xeroNetProfit)}
                        {p.difference !== null && p.difference !== 0 && (
                          <div className="text-rose-700 dark:text-rose-400">Differs by {exact(p.difference)}</div>
                        )}
                      </td>
                    ))}
                    {showChange && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {data && periods.length > 0 && (
            <div className={`flex gap-2 text-xs ${allTie ? 'text-muted-foreground' : 'text-rose-700 dark:text-rose-400'}`}>
              {allTie ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
              <span>
                {allTie
                  ? 'Profit before the tax allowance equals Xero’s own net profit to the cent in every period shown.'
                  : 'In at least one period the regrouped figures do not add back to Xero’s net profit. Refresh from Xero; if it stays, an account is being read twice or missed.'}
                {' '}Tax allowance is {Math.round(Number(periods[0].taxRate ?? 0) * 100)}% of a profit before tax, less any income tax already booked, and nil in a loss. Open a line to see its accounts.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
